import cx from "classnames";
import { handleCssTransition } from "@app/shared/qes-standards/utility/handleCssTransition";
import Portal from "@app/shared/Portal";
/* oxlint-disable-next-line import/no-cycle */
import EcdExportWrapper from "@app/overviews/enterprise-compliance-dashboard/Exports/EcdExportWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { commonFocusableElements } from "@app/shared/utility/commonFocusableElements";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { JSX, RefObject, FC, useState, useRef, useEffect } from "react";

interface EcdPreviewModalProps {
  body: JSX.Element;
  closeModal: () => void;
  isVisible: boolean;
  title: string;
  triggerElement?: RefObject<HTMLElement>;
}

const EcdPreviewModal: FC<EcdPreviewModalProps> = (props: EcdPreviewModalProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [delayedAction, setDelayedAction] = useState<() => void>();
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableElRef = useRef<HTMLElement>(null);
  const lastFocusableElRef = useRef<HTMLElement>(null);
  const hasBeenVisible = useRef(false);

  const handleAction = (action: () => void) => {
    setIsVisible(false);
    setDelayedAction(() => action);
  }

  useEffect(() => {
    if (isVisible) {
      hasBeenVisible.current = true;
    }
  }, [isVisible]);

  useEffect(() => {
    if (hasBeenVisible.current) {
      handleCssTransition(modalRef, "modal", !isVisible);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      const focusableEls = [...modalRef.current?.querySelectorAll(commonFocusableElements)] as HTMLElement[];
      firstFocusableElRef.current = focusableEls[0];
      lastFocusableElRef.current = focusableEls[focusableEls.length - 1];
      firstFocusableElRef.current.focus();
    } else {
      if (props.triggerElement?.current && hasBeenVisible.current) {
        props.triggerElement.current.focus();
      } else if (hasBeenVisible.current) {
        focusFirstInteractiveElement();
      }
    }
  }, [isVisible]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          if (isVisible) {
            handleAction(props.closeModal);
          }
          break;
        case "Tab":
          // don't let the user tab out of this modal
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElRef.current) {
              lastFocusableElRef.current?.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusableElRef.current) {
              firstFocusableElRef.current?.focus();
              e.preventDefault();
            }
          }
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isVisible]);

  useEffect(() => {
    if (props.isVisible) {
      setIsMounted(true);
      window.setTimeout(() => setIsVisible(true), 50);
    }
  }, [props.isVisible]);

  useEffect(() => {
    if (!!delayedAction && !isVisible) {
      window.setTimeout(() => {
        delayedAction();
        setIsMounted(false);
      }, 300); // 300ms is the duration of the css animation
    }
  }, [isVisible, delayedAction]);

  return (
    isMounted && (
      <Portal
        hideBodyScroll={true}
      >
        <div
          className={cx({
            "new-modal ecd-export-modal__modal_open": true,
          })}
          ref={modalRef}
        >
          <div
            className="new-modal__backdrop"
            data-testid="new-modal-backdrop"
            onClick={() => handleAction(props.closeModal)}
          />
          <div className="new-modal__modal ecd-export-modal__modal" onClick={(e) => e.stopPropagation()}>
            <>
              <div className="new-modal__actions">
                <h5 className="ecd-export-modal__title">{"Page Preview: " + props.title.replace(/([A-Z])/g, ' $1').trim()}</h5>
                <QESButton qesTestId="ecd-modal-close-button" className="ecd-preview-button" qesType="secondary" onClick={() => handleAction(props.closeModal)} aria-label="Close Popup">
                  <QesIcon iconName={QesIcons.X} description="Close preview" width="32" className="align-top" />
                </QESButton>
              </div>
              <div className="ecd-export-modal__body">
                <div className="ecd-export-modal__content_scale">
                  <EcdExportWrapper showExpand={false} tileContent={props.body} />
                </div>
              </div>
            </>
          </div>
        </div>
      </Portal>
    )
  );
}

export default EcdPreviewModal;